import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { useGreenhouseForm } from '@Router/EsgGreenhouse/Containers/EsgGreenhouseFormProvider';
import { ICarbonDetailFormValues } from '@Router/EsgGreenhouse/Containers/EsgGreenhouseFormProvider/types';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { modalAddCarbonValidationSchema } from '../validator';
import BaseYear from './BaseYear';
import GoalYear from './GoalYear';
import NeutralityVerification from './NeutralityVerification';
import Scope from './Scope';
import { checkCarbonVerificationComplete, mapCarbonFormValues } from './utils';

type ModalCarbonNeutralityProps = {
  open: boolean;
  close: () => void;
  mode: 'create' | 'edit';
  rowId?: number;
  editData?: ICarbonDetailFormValues;
};
const defaultFormValues = {
  scopeId: '',
  organizationId: '',
  organizationTh: '',
  organizationEn: '',
};

const ModalCarbonNeutrality = ({
  mode,
  open,
  close,
  rowId,
  editData,
}: ModalCarbonNeutralityProps) => {
  const initialValue =
    mode === 'edit' && editData
      ? mapCarbonFormValues(editData)
      : defaultFormValues;

  const modalCarbonNeutralityContext = useForm<ICarbonDetailFormValues>({
    defaultValues: initialValue,
    resolver: zodResolver(modalAddCarbonValidationSchema),
    mode: 'onChange',
  });

  const {
    handleSubmit,
    unregister,
    formState: { isValid },
  } = modalCarbonNeutralityContext;
  const { setValue, control, trigger, getValues } = useGreenhouseForm();

  const completedSection = useWatch({
    name: 'greenhouseGoalTerm.completedSection',
  });
  const { isCompletedTh, isCompletedEn } = completedSection ?? {};

  const handleClose = () => {
    unregister();
    close();
  };
  const isCarbonVerificationComplete = checkCarbonVerificationComplete(
    initialValue as ICarbonDetailFormValues,
    isCompletedTh,
    isCompletedEn
  );

  const handleSave = (data: ICarbonDetailFormValues) => {
    const prevData = getValues('greenhouseGoalTerm.carbon.details') || [];
    const updateCarbonDetails =
      mode === 'edit' && rowId !== undefined
        ? prevData.map((item, index) => (index === rowId ? data : item))
        : [...prevData, data];

    setValue('greenhouseGoalTerm.carbon.details', updateCarbonDetails);
    trigger('greenhouseGoalTerm.carbon.details');
    handleClose();
  };
  return (
    <FormProvider {...modalCarbonNeutralityContext}>
      <Dialog open={open} maxWidth="md">
        <DialogTitle fontWeight={'bold'} fontSize={16}>
          {mode === 'create' ? 'เพิ่ม' : 'แก้ไข'}
          รายละเอียดการตั้งเป้าหมายความเป็นกลางทางคาร์บอน (Carbon Neutrality)​
        </DialogTitle>
        <DialogContent>
          <Typography fontWeight={'bold'}>
            ขอบเขตการปล่อยก๊าซเรือนกระจก
          </Typography>
          <Scope control={control} mode={mode} />
          <Typography fontWeight={'bold'} mt={4}>
            เป้าหมายลดการปล่อยก๊าซเรือนกระจกเทียบกับปีฐาน
          </Typography>
          <BaseYear
            isCompletedTh={isCompletedTh}
            isCompletedEn={isCompletedEn}
          />
          <GoalYear
            isCompletedTh={isCompletedTh}
            isCompletedEn={isCompletedEn}
          />
          <NeutralityVerification control={control} />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button color="inherit" onClick={handleClose}>
            ยกเลิก
          </Button>
          <Button
            onClick={handleSubmit(handleSave)}
            disabled={!isValid || !isCarbonVerificationComplete}
          >
            ยืนยัน
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default ModalCarbonNeutrality;
